import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionCard, Pill } from "../Ui";
import { formatListDate } from "../../lib/purchaseListMeta";
import { PADDY_LAB_METRICS, formatPaddyLabMetric, paddyLabMetricClass } from "../../lib/paddyLabConfig";
import { hasRole, isAdmin, useAuth, hasPermission } from "../../context/AuthContext";
import LabReportReceiptActions from "./LabReportReceiptActions";
import AdminPaddyLabReportEditor from "./AdminPaddyLabReportEditor";

export default function PaddyLabReportRegister({
  title,
  reports = [],
  kind = "truck",
  testId = "lab-register",
  onReload,
}) {
  const { user } = useAuth();
  const adminUser = isAdmin(user);
  const canSeeVerdict = adminUser || hasRole(user, "manager");
  const canOpenPurchase = hasPermission(user, "page.purchases");
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const editing = reports.find((r) => String(r.id) === String(editId));

  const needsCorrection = (r) =>
    Boolean(r.validation_pending || r.needs_admin_review)
    || ["PENDING_REVIEW", "RELAB_REVIEW"].includes(String(r.verdict || "").toUpperCase());

  const filteredReports = useMemo(() => {
    if (statusFilter === "needs_correction") return reports.filter(needsCorrection);
    return reports;
  }, [reports, statusFilter]);

  const correctionCount = reports.filter(needsCorrection).length;

  const verdictTone = (r) => {
    if (needsCorrection(r)) return "warning";
    if (["AUTO_PASS", "PASS", "RECORDED", "APPROVED"].includes(r.verdict) || r.status_label === "Recorded") {
      return "success";
    }
    if (["REJECTED", "FAIL"].includes(r.verdict)) return "danger";
    return "warning";
  };

  const verdictLabel = (r) => {
    if (r.status_label) return r.status_label;
    if (needsCorrection(r)) return "Needs correction";
    if (canSeeVerdict && r.needs_admin_review) return "Needs admin review";
    if (!r.verdict || r.verdict === "RECORDED") return "Recorded";
    return String(r.verdict).replace(/_/g, " ");
  };

  const canEditRow = (r) => adminUser || needsCorrection(r);

  return (
    <SectionCard title={title} testId={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-xs text-muted-foreground">
          Date, vehicle, party, and quality readings — print or download PDF for records.
        </p>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={`rounded-md border px-2.5 py-1 text-xs ${
              statusFilter === "all" ? "border-bansal-sage bg-bansal-sage/10 text-foreground" : "border-border text-muted-foreground"
            }`}
            data-testid={`${testId}-filter-all`}
          >
            All ({reports.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("needs_correction")}
            className={`rounded-md border px-2.5 py-1 text-xs ${
              statusFilter === "needs_correction"
                ? "border-amber-400 bg-amber-50 text-amber-950"
                : "border-border text-muted-foreground"
            }`}
            data-testid={`${testId}-filter-correction`}
          >
            Needs correction ({correctionCount})
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm min-w-[960px]" data-testid={`${testId}-table`}>
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Vehicle</th>
              <th className="px-3 py-2.5">Party</th>
              <th className="px-3 py-2.5">Purchase</th>
              <th className="px-3 py-2.5">Phase</th>
              {PADDY_LAB_METRICS.slice(0, 4).map((m) => (
                <th key={m.key} className="px-3 py-2.5 whitespace-nowrap">{m.label}</th>
              ))}
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.map((r) => (
              <tr
                key={r.id}
                className={`border-t border-border hover:bg-muted/20 ${needsCorrection(r) ? "bg-amber-50/40" : ""}`}
              >
                <td className="px-3 py-2.5 text-xs whitespace-nowrap">{formatListDate(r.created_at) || "—"}</td>
                <td className="px-3 py-2.5 text-xs font-medium uppercase">{r.vehicle_number || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{r.party_name || "—"}</td>
                <td className="px-3 py-2.5 text-xs font-mono">
                  {r.purchase_id ? (
                    canOpenPurchase ? (
                      <Link to={`/purchases/${r.purchase_id}`} className="link-brand">
                        {r.purchase_code || r.purchase_id}
                      </Link>
                    ) : (
                      r.purchase_code || r.purchase_id
                    )
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                  {kind === "composite"
                    ? "Composite"
                    : r.sample_phase === "RELAB"
                      ? "Relab"
                      : "Pre-unloading"}
                </td>
                {PADDY_LAB_METRICS.slice(0, 4).map((m) => (
                  <td key={m.key} className={`px-3 py-2.5 text-xs tabular-nums ${paddyLabMetricClass(r, m.key)}`}>
                    {formatPaddyLabMetric(r, m)}
                  </td>
                ))}
                <td className="px-3 py-2.5">
                  <Pill tone={verdictTone(r)}>{verdictLabel(r)}</Pill>
                </td>
                <td className="px-3 py-2.5">
                  <LabReportReceiptActions
                    reportId={r.id}
                    kind={kind}
                    label={r.vehicle_number || r.purchase_code}
                    canEdit={canEditRow(r)}
                    onEdit={() => setEditId(r.id)}
                  />
                </td>
              </tr>
            ))}
            {filteredReports.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-10 text-muted-foreground">
                  {statusFilter === "needs_correction"
                    ? "No reports need correction."
                    : "No lab reports yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && canEditRow(editing) && (
        <div className="mt-4">
          <AdminPaddyLabReportEditor
            report={editing}
            kind={kind}
            labCorrection={!adminUser}
            onSaved={() => {
              setEditId(null);
              onReload?.();
            }}
            onCancel={() => setEditId(null)}
          />
        </div>
      )}
    </SectionCard>
  );
}
